import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import FormDialog from '../../../components/ui/FormDialog'
import { FormGrid } from '../../../components/ui/FormField'
import FormTextField from '../../../components/ui/form/FormTextField'
import { useZodForm } from '../../../lib/forms/useZodForm'
import {
  GROUP_NAME_MAX_LEN,
  templateParamGroupFormSchema,
  type TemplateParamGroupFormValues,
} from './templateParamGroupFormSchema'
import type { TemplateParamGroup } from '../types'

const EMPTY: TemplateParamGroupFormValues = { nameTr: '', nameEn: '' }

export interface TemplateParamGroupFormDialogProps {
  visible: boolean
  /** Düzenlenen grup. Kapalıyken `null` olur — form **yalnız düzenleme** içindir. */
  initial: TemplateParamGroup | null
  submitting?: boolean
  onSubmit: (values: TemplateParamGroupFormValues) => void
  onHide: () => void
}

/**
 * Parametre Grubu **düzenleme** formu (059 US5).
 *
 * <p><b>Yeni grup ekleme yoktur</b> (2026-08-03 kararı): gruplar parametrelerin bağlı olduğu sabit bir
 * sınıflandırmadır; ekleme/silme veri tabanı tarafında yapılır, ekranda yalnız ad düzenlenir ve durum
 * değiştirilir. Bu yüzden başlık daima "Düzenle"dir ve `initial` yalnız kapalıyken `null` olur.
 */
export default function TemplateParamGroupFormDialog({
  visible,
  initial,
  submitting,
  onSubmit,
  onHide,
}: Readonly<TemplateParamGroupFormDialogProps>) {
  const { t } = useTranslation()
  const { control, handleSubmit, reset } = useZodForm(templateParamGroupFormSchema, {
    defaultValues: EMPTY,
  })

  useEffect(() => {
    if (!visible) return
    reset(initial ? { nameTr: initial.nameTr, nameEn: initial.nameEn ?? '' } : EMPTY)
  }, [visible, initial, reset])

  return (
    <FormDialog
      visible={visible}
      header={t('admin.templateParameters.groups.form.editTitle')}
      submitLabel={t('admin.templateParameters.groups.form.save')}
      submitting={submitting}
      onSubmit={handleSubmit(onSubmit)}
      onHide={onHide}
    >
      <FormGrid>
        <FormTextField
          control={control}
          name="nameTr"
          label={t('admin.templateParameters.groups.form.nameTr')}
          required
          maxLength={GROUP_NAME_MAX_LEN}
        />
        <FormTextField
          control={control}
          name="nameEn"
          label={t('admin.templateParameters.groups.form.nameEn')}
          maxLength={GROUP_NAME_MAX_LEN}
        />
      </FormGrid>
    </FormDialog>
  )
}
